import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PromisePool } from 'es6-promise-pool';
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIgetAllDuplicates,
  APIbulkMergeDuplicate,
} from '../../../api/Files';

import ProcessingSpinner from '../../ProcessingSpinner';

import { sendTrackingEvent } from '../../../helpers';

import ErrorBoundary from '../../ErrorBoundary';
import Loading from '../../Loading';
import LoadingSmall from '../../LoadingSmall';

const BulkMergeModal = ({
  mode,
  handleClose,
  afterMerge,
}) => {
  const [duplicates, setDuplicates] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [merged, setMerged] = useState(0);
  const [done, setDone] = useState(false);

  const intl = useIntl();

  useEffect(() => {
    sendTrackingEvent('duplicate-bulk-merge-opened', { mode });
  }, [mode]);

  useEffect(() => {
    APIgetAllDuplicates({ mode }).then((response) => {
      setDuplicates(response.data.duplicates);
      setTotals(response.data.totals);
      setLoading(false);
    });
  }, [mode]);

  const getDuplicatesPromisePool = () => {
    let counterDuplicates = 0;
    const promiseProducerDuplicates = () => {
      if (counterDuplicates < duplicates.length) {
        const duplicate = duplicates[counterDuplicates];
        counterDuplicates += 1;
        return APIbulkMergeDuplicate(duplicate.uuid).then(() => {
          setMerged(counterDuplicates);
        }).catch(() => {});
      }
      return null;
    };

    return new PromisePool(promiseProducerDuplicates, 1);
  };

  const handleMerge = () => {
    setMerging(true);
    getDuplicatesPromisePool().start().then(() => {
      afterMerge();
      setMerging(false);
      setDone(true);

      sendTrackingEvent('duplicate-bulk-merge-completed');
    });
  };

  const getModalTitle = () => (
    (mode === 'images') ? intl.formatMessage({ defaultMessage: 'Bulk merge images' }) : intl.formatMessage({ defaultMessage: 'Bulk merge files' })
  );

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {getModalTitle()}
      </ModalHeader>
      <ModalBody className="modal-body-split">
        {loading ? (
          <Loading />
        ) : (
          <>
            {!(merging || done) && (
              <div className="modal-body-sidebar">
                <div className="modal-body-sidebar-header">
                  <div className="merge-files-summary">
                    <p className="center">
                      {intl.formatMessage({ defaultMessage: 'You are going to bulk merge {duplicates} duplicate {mode}.' }, {
                        duplicates: totals[mode],
                        mode,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="modal-body-content">
              <div className="merge-files-content">
                {merging && (
                  <div className="center">
                    <h5>{intl.formatMessage({ defaultMessage: 'Merging - please wait...' })}</h5>
                    <LoadingSmall />
                  </div>
                )}
                {done && (
                  <div className="center">
                    <h5>{intl.formatMessage({ defaultMessage: 'All duplicates have been merged!' })}</h5>
                    <p>
                      <Button
                        color="secondary"
                        onClick={handleClose}
                      >
                        {intl.formatMessage({ defaultMessage: 'Close' })}
                      </Button>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {!done && (
          <div className="buttons-group">
            <Button
              color="secondary"
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={merging || loading}
              onClick={handleMerge}
            >
              <ProcessingSpinner show={merging} />
              {merging ? (
                intl.formatMessage({ defaultMessage: 'Merging ({merged} of {total})...' }, { merged, total: totals[mode] })
              ) : (
                intl.formatMessage({ defaultMessage: 'Merge' })
              )}
            </Button>
          </div>
        )}
      </ModalFooter>
    </ErrorBoundary>
  );
};

BulkMergeModal.propTypes = {
  mode: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  afterMerge: PropTypes.func,
};

BulkMergeModal.defaultProps = {
  afterMerge: () => {},
};

export default connect()(BulkMergeModal);
